// Methods below are taken from: https://github.com/jasonleibowitz/react-add-to-calendar-hoc library
// Feature proposal: https://github.com/jasonleibowitz/react-add-to-calendar-hoc/issues/47

import { isBrowser } from "datastory-shared/utils/browser"

const isInternetExplorer = () =>
  /MSIE/.test(window.navigator.userAgent) ||
  /Trident/.test(window.navigator.userAgent)

const isMobile = () =>
  /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(
    window.navigator.userAgent || window.navigator.vendor || window.opera
  )

const escapeICSDescription = description =>
  description.replace(/(\r?\n|<br ?\/?>)/g, "\\n")

const formatDate = date => date && date.replace("+00:00", "Z")

const formatDuration = duration => {
  if (typeof duration === "string") return duration
  const parts = duration.toString().split(".")
  if (parts.length < 2) {
    parts.push("00")
  }

  return parts.map(part => (part.length === 2 ? part : `0${part}`)).join("")
}

const buildShareFile = ({
  description = "",
  endDatetime,
  location = "",
  startDatetime,
  timezone = "",
  title = "",
}) => {
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `URL:${document.URL}`,
    "METHOD:PUBLISH",
    timezone === ""
      ? `DTSTART:${startDatetime}`
      : `DTSTART;TZID=${timezone}:${startDatetime}`,
    timezone === ""
      ? `DTEND:${endDatetime}`
      : `DTEND;TZID=${timezone}:${endDatetime}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${escapeICSDescription(description)}`,
    `LOCATION:${location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n")

  return isMobile()
    ? encodeURI(`data:text/calendar;charset=utf8,${content}`)
    : content
}

const googleShareUrl = ({
  description,
  endDatetime,
  location,
  startDatetime,
  timezone,
  title,
}) =>
  `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${startDatetime}/${endDatetime}${
    timezone && `&ctz=${timezone}`
  }&location=${location}&text=${title}&details=${description}`

export const buildShareUrl = (
  {
    description = "",
    duration,
    endDatetime,
    location = "",
    startDatetime,
    timezone = "",
    title = "",
  },
  type
) => {
  if (!isBrowser) return

  const encodeURI = type !== SHARE_SITES.ICAL && type !== SHARE_SITES.OUTLOOK

  const data = {
    description: encodeURI ? encodeURIComponent(description) : description,
    duration: formatDuration(duration),
    endDatetime: formatDate(endDatetime),
    location: encodeURI ? encodeURIComponent(location) : location,
    startDatetime: formatDate(startDatetime),
    timezone,
    title: encodeURI ? encodeURIComponent(title) : title,
  }

  switch (type) {
    case SHARE_SITES.GOOGLE:
      return googleShareUrl(data)
    default:
      return buildShareFile(data)
  }
}

export const handleCalendarClick = (e, url, filename) => {
  if (!isBrowser) return

  if (e) e.preventDefault()

  if (url.startsWith("BEGIN")) {
    const blob = new Blob([url], { type: "text/calendar;charset=utf-8" })

    if (isInternetExplorer()) {
      window.navigator.msSaveOrOpenBlob(blob, `${filename}.ics`)
    } else {
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.setAttribute("download", `${filename}.ics`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  } else {
    window.open(url, "_blank")
  }
}

const SHARE_SITES = {
  GOOGLE: "Google",
  ICAL: "iCal",
  OUTLOOK: "Outlook",
}

export const SHARE_SITES_ITEMS = Object.keys(SHARE_SITES).map(
  i => SHARE_SITES[i]
)
